/**
 * Pure helpers: correlate queue timeout / miss rows with failover-extension CDR
 * and agent-missed activity. Shared by the Failover Follow-through report and
 * unit tests. Convex ingest duplicates a thin subset for Node isolation.
 */

export type FailoverLegOutcome =
  | 'answered'
  | 'no_answer'
  | 'busy'
  | 'voicemail'
  | 'no_leg'
  | 'not_applicable'
  | 'unknown';

export type QueueTimeoutLike = {
  id?: string;
  time: string;
  timestamp?: number;
  call_from: string;
  queue_name?: string;
  queue_num?: string;
  status: string;
  ring_duration: number;
  talk_duration?: number;
};

export type CdrLike = {
  time: string;
  timestamp?: number;
  call_from: string;
  call_to: string;
  disposition: string;
  talk_duration: number;
  /** Present on some Yeastar CDR builds */
  ring_duration?: number;
  call_type?: string;
};

export type AgentMissLike = {
  time: string;
  timestamp?: number;
  agent_name?: string;
  agent_num: string;
  src_num?: string;
  call_from?: string;
  queue?: string;
  queue_status?: string;
  total_wait_time?: number;
  polling_attempts?: number;
};

export type FailoverMatch = {
  outcome: FailoverLegOutcome;
  matchedCdrTime?: string;
  matchedDisposition?: string;
  ringSeconds?: number;
  talkSeconds?: number;
  busyDuringFailover: boolean;
  /** True when a CDR leg matched the caller near the failover window */
  matched: boolean;
  summary: string;
};

/** Digits only; strip leading international 00 / +. */
export function normalizePhoneDigits(value: string | undefined | null): string {
  if (!value) return '';
  let digits = String(value).replace(/[^\d]/g, '');
  if (digits.startsWith('00') && digits.length > 8) digits = digits.slice(2);
  return digits;
}

/** True if two phone strings refer to the same caller (handles 0 vs 27 prefix). */
export function phonesLikelyMatch(a: string, b: string): boolean {
  const da = normalizePhoneDigits(a);
  const db = normalizePhoneDigits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  const strip0 = (d: string) => (d.startsWith('0') && d.length >= 9 ? d.slice(1) : d);
  const strip27 = (d: string) => (d.startsWith('27') && d.length >= 11 ? d.slice(2) : d);
  const na = strip27(strip0(da));
  const nb = strip27(strip0(db));
  if (na && nb && na === nb) return true;
  // Suffix match for long numbers (last 9 digits)
  if (da.length >= 9 && db.length >= 9) {
    if (da.slice(-9) === db.slice(-9)) return true;
  }
  return false;
}

export function parseCallTimeMs(time: string, timestamp?: number | string): number {
  if (timestamp != null && Number(timestamp) > 0) {
    const n = Number(timestamp);
    if (n > 1e15) return Math.floor(n / 1000);
    if (n > 1e12) return n;
    // Yeastar often returns unix seconds
    if (n > 1e9) return n * 1000;
    return n * 1000;
  }
  const raw = String(time || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw.replace(/-/g, '/'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function normalizeQueueStatus(
  status: string
): 'answered' | 'missed' | 'abandoned' | 'busy' | 'other' {
  const s = String(status || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (s.includes('abandon') || s === 'abandond') return 'abandoned';
  if (s.includes('busy')) return 'busy';
  if (s.includes('miss') || s.includes('noanswer') || s === 'noanswered' || s === 'noansw') {
    return 'missed';
  }
  if (s.includes('answer') && !s.includes('no')) return 'answered';
  return 'other';
}

/** Queue max wait reached (failover should fire). Small slack for clock skew. */
export function hitQueueTimeout(
  waitSeconds: number,
  maxWaitSeconds?: number | string | null,
  slackSeconds = 3
): boolean {
  const max = Number(maxWaitSeconds);
  if (!Number.isFinite(max) || max <= 0) return false;
  return waitSeconds + slackSeconds >= max;
}

export function classifyCdrDisposition(disposition: string, talkSeconds = 0): FailoverLegOutcome {
  const d = String(disposition || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (d.includes('voicemail') || d.includes('vm')) return 'voicemail';
  if (d.includes('busy')) return 'busy';
  if (d.includes('answer') && !d.includes('no') && talkSeconds > 0) return 'answered';
  if (d.includes('answer') && !d.includes('no')) return 'answered';
  if (
    d.includes('noanswer') ||
    d.includes('miss') ||
    d.includes('cancel') ||
    d.includes('fail')
  ) {
    return 'no_answer';
  }
  if (talkSeconds > 0) return 'answered';
  return 'unknown';
}

export function recordTouchesExtension(record: CdrLike, extNumber: string): boolean {
  const ext = String(extNumber || '').trim();
  if (!ext) return false;
  const from = String(record.call_from || '');
  const to = String(record.call_to || '');
  // Extension appears as dial number or "Name <3201>" style
  const re = new RegExp(`(?:^|[^\\d])${ext}(?:[^\\d]|$)`);
  return re.test(from) || re.test(to) || from.includes(ext) || to.includes(ext);
}

/**
 * Was the failover extension already on an answered call when overflow was offered?
 */
export function wasBusyDuringWindow(
  cdrs: CdrLike[],
  extNumber: string,
  windowStartMs: number,
  windowEndMs: number
): boolean {
  for (const rec of cdrs) {
    if (!recordTouchesExtension(rec, extNumber)) continue;
    const outcome = classifyCdrDisposition(rec.disposition, rec.talk_duration || 0);
    if (outcome !== 'answered' || !(rec.talk_duration > 0)) continue;
    const start = parseCallTimeMs(rec.time, rec.timestamp);
    if (!start) continue;
    const end = start + (rec.talk_duration || 0) * 1000;
    // Overlap with failover offer window
    if (start < windowEndMs && end > windowStartMs) {
      // Exclude the failover leg itself if it's the only match later
      return true;
    }
  }
  return false;
}

export type MatchFailoverOptions = {
  /** ms before expected failover start */
  beforeMs?: number;
  /** ms after expected failover start */
  afterMs?: number;
  failoverExtNumber?: string;
  failoverLabel?: string;
  maxWaitSeconds?: number | string | null;
};

/**
 * Match one queue timeout row to a failover-extension CDR leg.
 * Queue "missed" after max wait does NOT imply the failover leg failed.
 */
export function matchFailoverLeg(
  queueRow: QueueTimeoutLike,
  cdrs: CdrLike[],
  options: MatchFailoverOptions = {}
): FailoverMatch {
  const failoverExt = String(options.failoverExtNumber || '').trim();
  const maxWait = options.maxWaitSeconds;
  const wait = Number(queueRow.ring_duration) || 0;
  const status = normalizeQueueStatus(queueRow.status);

  if (!failoverExt) {
    return {
      outcome: 'not_applicable',
      matched: false,
      busyDuringFailover: false,
      summary:
        options.failoverLabel && !/extension/i.test(options.failoverLabel)
          ? `Failover is configured as ${options.failoverLabel} (not a single extension — cannot match CDR legs).`
          : 'No extension failover configured for this queue.',
    };
  }

  // Abandoned before max wait usually never reaches failover
  if (status === 'abandoned' && !hitQueueTimeout(wait, maxWait)) {
    return {
      outcome: 'not_applicable',
      matched: false,
      busyDuringFailover: false,
      summary: `Caller hung up after ${wait}s before queue max wait — failover to ${failoverExt} typically did not fire.`,
    };
  }

  if (status === 'answered') {
    return {
      outcome: 'not_applicable',
      matched: false,
      busyDuringFailover: false,
      summary: 'Answered in queue — failover not used.',
    };
  }

  const beforeMs = options.beforeMs ?? 15_000;
  const afterMs = options.afterMs ?? 120_000;
  const queueStart = parseCallTimeMs(queueRow.time, queueRow.timestamp);
  const expectedFailover = queueStart + wait * 1000;
  const windowStart = expectedFailover - beforeMs;
  const windowEnd = expectedFailover + afterMs;

  const candidates = cdrs.filter((rec) => {
    if (!recordTouchesExtension(rec, failoverExt)) return false;
    // Caller should match call_from (external) on the failover leg
    const callerOnFrom = phonesLikelyMatch(queueRow.call_from, rec.call_from);
    const callerOnTo = phonesLikelyMatch(queueRow.call_from, rec.call_to);
    if (!callerOnFrom && !callerOnTo) return false;
    const t = parseCallTimeMs(rec.time, rec.timestamp);
    if (!t) return false;
    return t >= windowStart && t <= windowEnd;
  });

  // Prefer the closest CDR to expected failover time
  candidates.sort((a, b) => {
    const da = Math.abs(parseCallTimeMs(a.time, a.timestamp) - expectedFailover);
    const db = Math.abs(parseCallTimeMs(b.time, b.timestamp) - expectedFailover);
    return da - db;
  });

  const busyDuring = wasBusyDuringWindow(
    cdrs,
    failoverExt,
    expectedFailover,
    expectedFailover + Math.max(wait, 30) * 1000
  );

  if (candidates.length === 0) {
    return {
      outcome: 'no_leg',
      matched: false,
      busyDuringFailover: busyDuring,
      summary: busyDuring
        ? `No CDR leg found for ${failoverExt} with this caller after queue timeout — extension was on another call around that time (busy).`
        : `No CDR leg found for ${failoverExt} with this caller near queue timeout — phone may not have been offered the overflow, or CDR did not attribute the leg to ${failoverExt}.`,
    };
  }

  const best = candidates[0];
  const talk = Number(best.talk_duration) || 0;
  const ring =
    best.ring_duration != null
      ? Number(best.ring_duration)
      : undefined;
  let outcome = classifyCdrDisposition(best.disposition, talk);

  // If we matched a concurrent answered call that is NOT the overflow (different talk window),
  // still report the matched leg disposition for the caller.
  const summaryParts: string[] = [];
  if (outcome === 'answered') {
    summaryParts.push(
      `Failover to ${failoverExt} was answered (talk ${talk}s). Queue still correctly shows as missed.`
    );
  } else if (outcome === 'no_answer') {
    summaryParts.push(
      `Failover to ${failoverExt} rang but was not answered${ring != null ? ` (ring ${ring}s)` : ''}.`
    );
  } else if (outcome === 'busy') {
    summaryParts.push(`Failover to ${failoverExt} returned busy.`);
  } else if (outcome === 'voicemail') {
    summaryParts.push(`Failover to ${failoverExt} went to voicemail.`);
  } else {
    summaryParts.push(
      `Failover leg on ${failoverExt}: ${best.disposition || 'unknown'}${talk ? ` (talk ${talk}s)` : ''}.`
    );
  }
  if (busyDuring && outcome !== 'busy') {
    summaryParts.push('Extension also had overlapping answered call activity.');
  }

  return {
    outcome,
    matched: true,
    matchedCdrTime: best.time,
    matchedDisposition: best.disposition,
    ringSeconds: ring,
    talkSeconds: talk,
    busyDuringFailover: busyDuring && outcome !== 'answered',
    summary: summaryParts.join(' '),
  };
}

export function agentLabel(name?: string, num?: string): string {
  const n = String(num || '').trim();
  const displayName = String(name || '').trim();
  if (displayName && n && displayName !== n) return `${displayName} (${n})`;
  return displayName || n || '';
}

/**
 * Attach agents who missed for a queue call using Agent Missed Call Activity rows.
 * Match by caller + time proximity (default ±90s).
 */
export function agentsWhoMissedForCall(
  queueRow: QueueTimeoutLike,
  agentMisses: AgentMissLike[],
  windowMs = 90_000
): string[] {
  const callMs = parseCallTimeMs(queueRow.time, queueRow.timestamp);
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const miss of agentMisses) {
    const missMs = parseCallTimeMs(miss.time, miss.timestamp);
    if (callMs && missMs && Math.abs(missMs - callMs) > windowMs) continue;
    const src = miss.src_num || miss.call_from || '';
    if (src && queueRow.call_from && !phonesLikelyMatch(queueRow.call_from, src)) continue;
    // If no src on miss row, still allow time-only match within window
    if (!src && callMs && missMs && Math.abs(missMs - callMs) > 30_000) continue;
    const label = agentLabel(miss.agent_name, miss.agent_num);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels;
}

export type FailoverSummaryCounts = {
  totalQueueMisses: number;
  timedOut: number;
  abandonedBeforeTimeout: number;
  failoverAnswered: number;
  failoverNoAnswer: number;
  failoverBusy: number;
  failoverVoicemail: number;
  failoverNoLeg: number;
  failoverNotApplicable: number;
  failoverUnknown: number;
  busyDuringCount: number;
};

export function emptyFailoverSummary(): FailoverSummaryCounts {
  return {
    totalQueueMisses: 0,
    timedOut: 0,
    abandonedBeforeTimeout: 0,
    failoverAnswered: 0,
    failoverNoAnswer: 0,
    failoverBusy: 0,
    failoverVoicemail: 0,
    failoverNoLeg: 0,
    failoverNotApplicable: 0,
    failoverUnknown: 0,
    busyDuringCount: 0,
  };
}

export function accumulateFailoverOutcome(
  counts: FailoverSummaryCounts,
  match: FailoverMatch,
  timedOut: boolean,
  abandonedBeforeTimeout: boolean
): void {
  counts.totalQueueMisses += 1;
  if (timedOut) counts.timedOut += 1;
  if (abandonedBeforeTimeout) counts.abandonedBeforeTimeout += 1;
  if (match.busyDuringFailover) counts.busyDuringCount += 1;
  switch (match.outcome) {
    case 'answered':
      counts.failoverAnswered += 1;
      break;
    case 'no_answer':
      counts.failoverNoAnswer += 1;
      break;
    case 'busy':
      counts.failoverBusy += 1;
      break;
    case 'voicemail':
      counts.failoverVoicemail += 1;
      break;
    case 'no_leg':
      counts.failoverNoLeg += 1;
      break;
    case 'not_applicable':
      counts.failoverNotApplicable += 1;
      break;
    default:
      counts.failoverUnknown += 1;
  }
}

export function formatFailoverOutcomeLabel(outcome: FailoverLegOutcome): string {
  switch (outcome) {
    case 'answered':
      return 'Failover answered';
    case 'no_answer':
      return 'Rang, no answer';
    case 'busy':
      return 'Failover busy';
    case 'voicemail':
      return 'Voicemail';
    case 'no_leg':
      return 'No CDR leg found';
    case 'not_applicable':
      return 'N/A';
    default:
      return 'Unknown';
  }
}
